import cn from 'classnames';
import Link from 'next/link';
import { KeyboardEvent, MouseEvent } from 'react';

import CloseIcon from '@icons/small/close.svg';

import styles from './Chip.module.scss';
import { ChipProps, ChipSize, ChipVariant } from './types';

const sizeClass: Record<ChipSize, string> = {
    xs: styles.chip_size_xs,
    md: styles.chip_size_md,
    lg: styles.chip_size_lg,
};

const variantClass: Record<ChipVariant, string> = {
    outline: styles.chip_variant_outline,
    outlineDark: styles.chip_variant_outlineDark,
    neutral: styles.chip_variant_neutral,
};

export const Chip = ({
    className,
    children,
    variant = 'outline',
    size = 'md',
    isActive = false,
    removable = false,
    onRemove,
    removeAriaLabel = 'Удалить',
    onClick,
    href,
    style,
    'data-testid': dataTestId,
    ...props
}: ChipProps) => {
    const canRemove = removable && Boolean(onRemove);
    const chipClassName = cn(
        styles.chip,
        sizeClass[size],
        variantClass[variant],
        isActive && styles.chip_state_active,
        className
    );

    const handleRemoveClick = (event: MouseEvent<HTMLSpanElement>) => {
        event.stopPropagation();
        onRemove?.();
    };

    const handleRemoveKeyDown = (event: KeyboardEvent<HTMLSpanElement>) => {
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            event.stopPropagation();
            onRemove?.();
        }
    };

    const content = (
        <>
            <span className={styles.chip__label}>{children}</span>
            {canRemove && (
                <span
                    role="button"
                    aria-label={removeAriaLabel}
                    tabIndex={0}
                    className={styles.chip__remove}
                    onClick={handleRemoveClick}
                    onKeyDown={handleRemoveKeyDown}
                >
                    <CloseIcon className={styles.chip__removeIcon} />
                </span>
            )}
        </>
    );

    return href ? (
        <Link href={href} prefetch={false} className={chipClassName} style={style} data-testid={dataTestId}>
            {content}
        </Link>
    ) : (
        <button
            type="button"
            onClick={onClick}
            className={chipClassName}
            style={style}
            data-testid={dataTestId}
            {...props}
        >
            {content}
        </button>
    );
};
